import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddItemForm } from "../components/AddItemForm.js";
import { ErrorToast } from "../components/ErrorToast.js";
import { ItemList } from "../components/ItemList.js";
import { type UndoItem, UndoToast } from "../components/UndoToast.js";
import { useListItems } from "../hooks/useListItems.js";
import * as api from "../lib/api.js";
import { MAX_ITEMS } from "../lib/constants.js";
import { getDisplayName } from "../lib/storage.js";
import type { ListItem, StoredList } from "../types/index.js";
import styles from "./ListPage.module.css";

interface Props {
  currentList: StoredList;
}

export function ListPage({ currentList }: Props) {
  const { items, loading, error } = useListItems(currentList.listId);
  const [undoItems, setUndoItems] = useState<UndoItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const displayName = getDisplayName();

  const handleAdd = useCallback(
    async (text: string) => {
      await api.addItem(currentList.shareToken, text, displayName);
    },
    [currentList.shareToken, displayName],
  );

  const handleDelete = useCallback(
    async (item: ListItem) => {
      try {
        const result = await api.deleteItem(currentList.shareToken, item.itemId);
        if (result.status === "deleted" && result.undoExpiresAt) {
          setUndoItems((prev) => [
            ...prev,
            {
              itemId: item.itemId,
              text: item.text,
              expiresAt: new Date(result.undoExpiresAt!).getTime(),
            },
          ]);
        }
      } catch {
        setErrorMessage("削除に失敗しました。");
      }
    },
    [currentList.shareToken],
  );

  const handleUndo = useCallback(
    async (itemId: string) => {
      try {
        await api.undoDeleteItem(currentList.shareToken, itemId);
        setUndoItems((prev) => prev.filter((u) => u.itemId !== itemId));
      } catch {
        setErrorMessage("元に戻す操作に失敗しました。");
      }
    },
    [currentList.shareToken],
  );

  const handleUndoExpire = useCallback((itemId: string) => {
    setUndoItems((prev) => prev.filter((u) => u.itemId !== itemId));
  }, []);

  const handleReorder = useCallback(
    async (movedId: string, prevId: string | null, nextId: string | null) => {
      try {
        await api.reorderItem(currentList.shareToken, movedId, prevId, nextId);
      } catch {
        setErrorMessage("並び替えに失敗しました。");
      }
    },
    [currentList.shareToken],
  );

  if (loading) {
    return (
      <main className={styles.loading}>
        <output>読み込み中…</output>
      </main>
    );
  }

  if (error) {
    console.error("Firestore subscription error:", error);
    return (
      <main className={styles.error} role="alert">
        アイテムの読み込みに失敗しました。接続を確認して再度お試しください。
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>{currentList.name || "買い物リスト"}</h1>
        <div className={styles.headerActions}>
          <output className={styles.count} aria-label={`${items.length}件 / ${MAX_ITEMS}件`}>
            {items.length}/{MAX_ITEMS}
          </output>
          <button type="button" className={styles.listsButton} onClick={() => navigate("/lists")}>
            リスト一覧
          </button>
        </div>
      </header>

      <ItemList items={items} onDelete={handleDelete} onReorder={handleReorder} />

      {undoItems.map((u) => (
        <UndoToast key={u.itemId} item={u} onUndo={handleUndo} onExpire={handleUndoExpire} />
      ))}

      {errorMessage && (
        <ErrorToast message={errorMessage} onDismiss={() => setErrorMessage(null)} />
      )}

      <AddItemForm
        onAdd={handleAdd}
        onError={setErrorMessage}
        disabled={items.length >= MAX_ITEMS}
      />
    </main>
  );
}
